
import Vue from "vue";
import { Component, Provide } from "vue-property-decorator";
import lodash from "lodash";
import { configStore, uiStore, globalStore } from "@/store/store";
import { ChangeControlValueMeta, ControlsTreeContext } from "@/types/controls";
import ControlsTree from "@/components/Reusable/ControlsTree/ControlsTree.vue";
import SearchBox, {
  SearchBoxState
} from "@/components/ElementPropertiesPanel/SearchBox.vue";
import defensiveCopy from "@/util/defensive-copy";
import {
  addSelectorToStoreAndSaveSelectorsToWordPressIfSelectorsPanelIsClosed,
  isValidSelectorName
} from "@/lib/selectors/selectors";
import IconButton from "@/components/UI/IconButton.vue";
import PanelHeader from "@/components/Reusable/Util/PanelHeader.vue";
import NotificationLogger from "@/lib/notifications/notifications";
import { openUiPanel } from "@/lib/panels/panels";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import {
  getSelectorAccordionState,
  setSelectorAccordionState
} from "@/lib/panels/accordions";
import { addMetadataToSelectorPropertyChangeBasingOnControlValueChangeMetadata } from "@/lib/controls/utils";
import { getBuilderCanvasDocument } from "@/lib/canvas/helpers";
import { filterControls, flattenAndCount } from "@/lib/controls/search";
import { addOrUpdateInlineCSSAtEndOfHead } from "@/lib/canvas/assets/dom-utils";
import SearchBoxEmptyState from "@/components/ElementPropertiesPanel/SearchBoxEmptyState.vue";
import Icon from "@/components/UI/Icon.vue";
import AlertBox from "@/components/Reusable/CustomInputs/AlertBox.vue";
import FreeModeAlertForSelectors from "@/components/Subscription/FreeModeAlertForSelectors.vue";

@Component({
  components: {
    FreeModeAlertForSelectors,
    AlertBox,
    ControlsTree,
    SearchBox,
    IconButton,
    PanelHeader,
    TextInput,
    SearchBoxEmptyState,
    Icon
  }
})
export default class EditSelector extends Vue {
  searchBoxState: SearchBoxState = {
    modifiedOnly: false,
    query: ""
  };

  updateSearchBoxState(newSearchBoxState: SearchBoxState) {
    this.searchBoxState = newSearchBoxState;
  }

  clearQuery() {
    this.updateSearchBoxState({
      modifiedOnly: false,
      query: ""
    });
  }

  get hasSearchParameters() {
    return this.searchBoxState.modifiedOnly || this.searchBoxState.query;
  }

  get controlsFilteredBySearchParams() {
    return filterControls(
      this.controls,
      this.searchBoxState.modifiedOnly,
      this.searchBoxState.query,
      this.editingClassControlsTreeContext,
      "properties"
    );
  }

  get noSearchResults() {
    return flattenAndCount(this.controlsFilteredBySearchParams, 0) === 0;
  }

  get howManyOnPage() {
    if (!uiStore.activeSelector) return 0;

    const selectorToFind = this.editingClassHover
      ? uiStore.activeSelector.name.replace(":hover", "")
      : uiStore.activeSelector.name;

    const matchingElements =
      window.Breakdance.canvas.document.querySelectorAll(selectorToFind);

    return matchingElements.length;
  }

  get editingClass() {
    if (!this.activeSelector) return false;
    return this.activeSelector.type === "class";
  }

  get editingClassHover() {
    if (!this.activeSelector) return false;
    if (this.activeSelector.name.endsWith(":hover")) {
      return !!findClass(this.activeSelector.name.replace(":hover", ""));
    }
    return false;
  }

  get editingClassOrClassHover() {
    return this.editingClass || this.editingClassHover;
  }

  get selectorIsHighlightable() {
    if (!uiStore.activeSelectorName) return false;

    if (uiStore.activeSelectorName.includes(":") && !this.editingClassHover)
      return false;
    return true;
  }

  get controls() {
    return configStore.selectorControls;
  }

  get activeSelector() {
    return uiStore.activeSelector;
  }

  highlight = false;

  toggleHighlight() {
    if (this.highlight) {
      this.clearHighlight();
    } else {
      this.setHighlight();
    }
  }

  beforeDestroy() {
    this.clearHighlight();
  }

  setHighlight() {
    this.highlight = true;

    if (!uiStore.activeSelector) return;

    const selectorToHighlight = this.editingClassHover
      ? uiStore.activeSelector.name.replace(":hover", "")
      : uiStore.activeSelector.name;

    addOrUpdateInlineCSSAtEndOfHead(
      getBuilderCanvasDocument(), // TODO this won't work with browse mode
      "breakdance-selector-highlight",
      `${selectorToHighlight} {
          outline: 10px dashed rgba(255,200,0) !important;
          outline-offset: -5px;
          animation-duration: 2000ms;
          animation-iteration-count: infinite;
        }
        `
    );
  }

  clearHighlight() {
    this.highlight = false;
    addOrUpdateInlineCSSAtEndOfHead(
      getBuilderCanvasDocument(), // TODO this won't work with browse mode
      "breakdance-selector-highlight",
      ""
    );
  }

  deleteSelector() {
    if (!this.activeSelector) return;

    const selector = this.activeSelector;
    const indexToDelete = globalStore.cssSelectors.findIndex(
      s => s.name === selector.name
    );

    if (indexToDelete !== -1) {
      const newSelectors = defensiveCopy(globalStore.cssSelectors);
      newSelectors.splice(indexToDelete, 1);
      globalStore.setSelectors(newSelectors);
    }
  }

  close() {
    this.backToAllSelectors();
  }

  get activeElementTitle() {
    if (!uiStore.activeElement) return false;
    return configStore.element(uiStore.activeElement.data.type).name;
  }

  openElementProperties() {
    openUiPanel("elementproperties");
  }

  backToAllSelectors() {
    uiStore.setActiveSelectorName(null);
  }

  attemptingToRename: string | false = false;

  get isAttemptedRenameValid() {
    if (!this.attemptingToRename) return true;

    return isValidSelectorName(this.attemptingToRename);
  }

  tryToRenameActiveSelector(newName: string | null) {
    if (!newName) return;

    this.attemptingToRename = newName;

    this.clearHighlight();

    if (!isValidSelectorName(newName)) return;

    const activeSelectorIndex = globalStore.cssSelectors.findIndex(
      s => s === this.activeSelector
    );

    const newSelectors = defensiveCopy(globalStore.cssSelectors);

    if (newSelectors[activeSelectorIndex]) {
      newSelectors[activeSelectorIndex].name = newName;
      globalStore.setSelectors(newSelectors);
      uiStore.setActiveSelectorName(newName);
      this.attemptingToRename = false;
    }
  }

  toggleHover() {
    if (!uiStore.activeSelector) return;

    if (this.editingClass) {
      const hoverSelectorName = uiStore.activeSelectorName + ":hover";

      const findHoverSelector = () =>
        globalStore.cssSelectors.find(s => s.name === hoverSelectorName);

      let hoverSelector = findHoverSelector();

      if (!hoverSelector) {
        addSelectorToStoreAndSaveSelectorsToWordPressIfSelectorsPanelIsClosed({
          name: hoverSelectorName,
          type: "custom",
          properties: {}
        });
        hoverSelector = findHoverSelector();
      }

      if (!hoverSelector) {
        NotificationLogger.log.message(
          `Unable to create or find a :hover selector for ${uiStore.activeSelectorName}`
        );
        return;
      }

      uiStore.setActiveSelectorName(hoverSelector.name);
      openUiPanel("selectors");
    } else if (this.editingClassHover && this.activeSelector) {
      const foundClass = findClass(
        this.activeSelector.name.replace(":hover", "")
      );
      if (foundClass) {
        uiStore.setActiveSelectorName(foundClass.name);
        openUiPanel("selectors");
      }
    }
  }

  @Provide("context")
  editingClassControlsTreeContext = editingClassControlsTreeContext;
}

const findClass = (className: string) =>
  globalStore.cssSelectors.find(selector => selector.name === className);

const editingClassControlsTreeContext: ControlsTreeContext = {
  dynamicProperty: () => false,
  getControls: () => configStore.selectorControls,
  getControlValue: controlPath => {
    return lodash.get(uiStore.activeSelector, `${controlPath}`, undefined);
  },
  setControlValue(
    controlPathProbablyWithBreakpointIdAndHoverSuffix,
    newValue,
    control,
    meta?: ChangeControlValueMeta
  ) {
    if (!uiStore.activeSelector) return;

    globalStore.throttledSelectorPropertyChange(
      addMetadataToSelectorPropertyChangeBasingOnControlValueChangeMetadata(
        {
          selector: uiStore.activeSelector,
          path: controlPathProbablyWithBreakpointIdAndHoverSuffix,
          value: newValue
        },
        {
          control,
          controlsTree:
            this.getControls(
              controlPathProbablyWithBreakpointIdAndHoverSuffix
            ) || []
        },
        meta
      )
    );
  },
  setAccordionState: (accordionPath: string, open: boolean) => {
    const activeSelectorName =
      uiStore.activeSelector && uiStore.activeSelector.name;
    if (!activeSelectorName) return;
    setSelectorAccordionState(activeSelectorName, accordionPath, open);
  },
  getAccordionState: (accordionPath: string) => {
    const activeSelectorName =
      uiStore.activeSelector && uiStore.activeSelector.name;
    if (!activeSelectorName) return false;
    return getSelectorAccordionState(activeSelectorName, accordionPath);
  }
};
